/**
 * Escala de cores estilo formatação condicional do Excel.
 * Quanto maior o valor, mais intenso o tom.
 * Entradas (receitas) = tons de verde, Saídas (despesas) = tons de vermelho.
 */

const GREEN_SCALE = [
    '#e6f5ec', // 0%   – quase branco esverdeado
    '#b3e6c7', // 12.5%
    '#80d6a1', // 25%
    '#4dc67c', // 37.5%
    '#22c55e', // 50%  – verde vibrante central
    '#1aab50', // 62.5%
    '#159243', // 75%
    '#107a38', // 87.5%
    '#0a6b2e', // 100% – verde escuro (mas ainda legível)
];

const RED_SCALE = [
    '#fce8e8', // 0%   – quase branco avermelhado
    '#f8c4c4', // 12.5%
    '#f09a9a', // 25%
    '#e87272', // 37.5%
    '#ef4444', // 50%  – vermelho vibrante central
    '#e83a3a', // 62.5%
    '#dc2626', // 75%
    '#cc1f1f', // 87.5%
    '#b91c1c', // 100% – vermelho forte (legível e claro)
];

function interpolateFromScale(scale: string[], ratio: number): string {
    const clamped = Math.max(0, Math.min(1, ratio));
    const idx = clamped * (scale.length - 1);
    const lower = Math.floor(idx);
    const upper = Math.ceil(idx);
    if (lower === upper) return scale[lower];
    const t = idx - lower;
    return lerpColor(scale[lower], scale[upper], t);
}

function lerpColor(a: string, b: string, t: number): string {
    const ar = parseInt(a.slice(1, 3), 16);
    const ag = parseInt(a.slice(3, 5), 16);
    const ab = parseInt(a.slice(5, 7), 16);
    const br = parseInt(b.slice(1, 3), 16);
    const bg = parseInt(b.slice(3, 5), 16);
    const bb = parseInt(b.slice(5, 7), 16);
    const r = Math.round(ar + (br - ar) * t);
    const g = Math.round(ag + (bg - ag) * t);
    const bl = Math.round(ab + (bb - ab) * t);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${bl.toString(16).padStart(2, '0')}`;
}

/**
 * Retorna a cor baseada no valor e no máximo do dataset.
 * @param value  - Valor absoluto do item
 * @param maxValue - Maior valor absoluto do dataset (para calcular o ratio)
 * @param type - 'income' para tons de verde, 'expense' para tons de vermelho
 */
export function getScaleColor(value: number, maxValue: number, type: 'income' | 'expense'): string {
    if (maxValue === 0) return '#FFFFFF';
    const ratio = Math.abs(value) / maxValue;
    const scale = type === 'income' ? GREEN_SCALE : RED_SCALE;
    return interpolateFromScale(scale, ratio);
}

/**
 * Gera um array de cores para um dataset, com escala proporcional ao maior valor.
 */
export function getScaleColors(values: number[], type: 'income' | 'expense'): string[] {
    const maxVal = Math.max(...values.map(Math.abs), 1);
    return values.map(v => getScaleColor(v, maxVal, type));
}

export function getScaleColorsByRange(values: number[], type: 'income' | 'expense'): string[] {
    if (values.length === 0) return [];

    const abs = values.map(Math.abs);
    const min = Math.min(...abs);
    const max = Math.max(...abs);
    const range = max - min;

    if (range === 0) {
        const scale = type === 'income' ? GREEN_SCALE : RED_SCALE;
        return values.map(() => scale[scale.length - 1]);
    }

    return abs.map((v) => {
        const ratio = (v - min) / range;
        const scale = type === 'income' ? GREEN_SCALE : RED_SCALE;
        return interpolateFromScale(scale, ratio);
    });
}

/**
 * Cor representativa da série, para onde não existe um valor a escalar:
 * ícone da legenda e cor do texto do tooltip. É o tom central de cada escala.
 */
export const SERIES_COLOR: Record<'income' | 'expense', string> = {
    income: GREEN_SCALE[4],
    expense: RED_SCALE[4],
};
